import React, { FC, MouseEventHandler, useContext, useEffect, useRef, useState } from 'react';
import { BKJButton, BKJFileInput, BKJPillDropdown } from '@bookjane2/bookjane-design-library';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { Flex } from 'components/Flex';
import {
  CSVBorderHeaderStyle,
  H3,
  TeamMemberCSVWrapper,
  LocationHeader,
  LocationName,
  DownloadHeader,
  UploadHeader,
  HeaderDescription,
} from 'components/InviteTeamMembersByCSVModal/views/EditRanking/TeamMemberEditRankingView.styled';
import {
  TeamMemberEditRankingFormProvider,
  useTeamMemberEditRankingFormContext,
} from 'components/InviteTeamMembersByCSVModal/views/EditRanking/TeamMemberEditRanking.form.context';
import { useTranslation } from 'hooks/useTranslation';
import { TeamMemberPageContext } from 'pages/TeamMemberPage/TeamMemberPage.context';
import {
  CommunitySeniorityMethodContext,
  CommunitySeniorityMethodProvider,
} from 'pages/TeamMemberPage/CommunitySeniorityMethod.context';
import { useSelector } from 'react-redux';
import { isMultiLocationTieringFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { PositionByDepartmentSelectInput } from 'components/PositionByDepartmentSelectInput';
import { IBKJComboBoxAsyncOption } from '@bookjane2/bookjane-design-library/lib/components/BKJComboBoxAsync/BKJComboBoxAsync.types';
import {
  TeamMemberEditRankingDataProvider,
  useTeamMemberEditRankingDataContext,
} from 'components/InviteTeamMembersByCSVModal/views/EditRanking/TeamMemberEditRanking.data.context';
import { DropdownItem } from 'components/JaneModal/views/EditJaneView/EditJaneView.constants';

const mergeOptions = (currentOptions: DropdownItem[], newOptions: DropdownItem[]) => {
  const merged = new Map(currentOptions.map((option) => [option.id, option]));

  newOptions.forEach((option) => {
    merged.set(option.id, option);
  });

  return Array.from(merged.values());
};

const EditRankingViewMultiLocationTieringView: FC = () => {
  const { onChange, values, fieldErrors, onDownload, onSubmit, isDisabled, isSubmitting } =
    useTeamMemberEditRankingFormContext();

  const {
    data: { options = [], hasNextPage },
    isLoading,
    loadMore,
  } = useTeamMemberEditRankingDataContext();
  const {
    values: { location },
  } = useContext(TeamMemberPageContext);
  const locationName = location?.label;
  const { t } = useTranslation();
  const {
    data: {
      fetchCommunity: {
        organization: { seniorityMethod },
      },
    },
  } = useContext(CommunitySeniorityMethodContext);

  const [dropdownData, setDropdownData] = useState(options);
  const prevOptionsRef = useRef(options);

  useEffect(() => {
    if (
      prevOptionsRef.current !== options &&
      JSON.stringify(prevOptionsRef.current) !== JSON.stringify(options)
    ) {
      setDropdownData((currentData) => mergeOptions(currentData, options));
      prevOptionsRef.current = options; // Update the ref to the new options
    }
  }, [options]);

  if (hasNextPage && !isLoading) {
    loadMore();
  }

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
      borderTop={CSVBorderHeaderStyle}
    >
      <Flex flexDirection="column" padding="32px 40px 15px" gap="32px">
        <H3>{t('team_member:IMPORT_RANKING')}</H3>
        <TeamMemberCSVWrapper>
          <LocationHeader>{`${t('team_member:LOCATION')} (${t(
            'team_member:CURRENT',
          )})`}</LocationHeader>
          <LocationName>{locationName}</LocationName>
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <LocationHeader>
            {t('team_member:SENIORITY_CALCULATED_IN')}:{' '}
            {t(`team_member:${seniorityMethod.toUpperCase()}`)}
          </LocationHeader>
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <DownloadHeader>1. {t('team_member:SELECT_LOCATIONS_FOR_SPREADSHEET')}</DownloadHeader>
          <HeaderDescription>{t('team_member:TEAM_MEMBERS_WILL_BE_RANKED')}</HeaderDescription>
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <LocationHeader>{t('team_member:SELECT_LOCATIONS_FOR_CSV')}</LocationHeader>
          <BKJPillDropdown
            name="location_ids"
            type="Multi"
            value={values.location_ids}
            onChange={onChange}
            options={dropdownData}
            max={dropdownData.length}
            showSelectAll
            validations={[]}
            placeholder={t('team_member:SELECT_LOCATIONS')}
          />
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <DownloadHeader>2. {t('team_member:DOWNLOAD_SPREADSHEET')}</DownloadHeader>
          <HeaderDescription>
            {t('team_member:EDIT_TEAM_MEMBER_RANKINGS_IN_THE_SPREADSHEET_TEMPLATE')}
          </HeaderDescription>
        </TeamMemberCSVWrapper>
        <BKJButton
          variant="PurpleOutlined"
          width="180px"
          onClick={onDownload}
          type="button"
          disabled={isDisabled}
        >
          {t('team_member:DOWNLOAD_CSV')}
        </BKJButton>
        <TeamMemberCSVWrapper>
          <UploadHeader>3. {t('team_member:UPLOAD_SPREADSHEET')}</UploadHeader>
          <HeaderDescription>
            {t('team_member:UPLOAD_SPREADSHEET_WITH_THE_NEW_RANKINGS')}
          </HeaderDescription>
        </TeamMemberCSVWrapper>
        <div style={{ width: '50%' }}>
          <BKJFileInput
            value={values?.csv}
            name="csv"
            onChange={onChange}
            accept=".csv"
            error={fieldErrors?.csv}
          />
        </div>
      </Flex>

      <Flex height="100%"></Flex>
      <BKJSideModalFooter justifyContent="space-between" flexDirection="row-reverse">
        <BKJButton
          variant="PurpleOutlined"
          width="180px"
          disabled={isDisabled || !values?.csv}
          onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
          type="button"
          isLoading={isSubmitting}
        >
          {t('team_member:SUBMIT')}
        </BKJButton>
      </BKJSideModalFooter>
    </Flex>
  );
};

const EditRankingViewTieringView: FC = () => {
  const { onChange, values, fieldErrors, onDownload, onSubmit, isDisabled, isSubmitting } =
    useTeamMemberEditRankingFormContext();
  const {
    values: { location },
  } = useContext(TeamMemberPageContext);
  const locationName = location?.label;
  const { t } = useTranslation();

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
      borderTop={CSVBorderHeaderStyle}
    >
      <Flex flexDirection="column" padding="32px 40px 15px" gap="32px">
        <H3>{t('team_member:IMPORT_RANKING')}</H3>
        <TeamMemberCSVWrapper>
          <LocationHeader>{`${t('team_member:LOCATION')} (${t(
            'team_member:CURRENT',
          )})`}</LocationHeader>
          <LocationName>{locationName}</LocationName>
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <LocationHeader>{t('team_member:SELECT_POSITION')}</LocationHeader>
          <PositionByDepartmentSelectInput
            location={location as IBKJComboBoxAsyncOption}
            dropdownVariant="Square"
            placeholder={t('team_member:SELECT_POSITION')}
            onChange={onChange}
            type="Single"
            name="position_id"
            variant="GreyOutlined"
            value={values?.position_id}
            error={fieldErrors?.position_id}
            width="442px"
            dropdownWidth="442px"
            dropdownProps={{ dropdownListItemVariant: 'List', isSearchable: false }}
          />
        </TeamMemberCSVWrapper>
        <TeamMemberCSVWrapper>
          <DownloadHeader>1. {t('team_member:DOWNLOAD_SPREADSHEET')}</DownloadHeader>
          <HeaderDescription>
            {t('team_member:EDIT_TEAM_MEMBER_RANKINGS_IN_THE_SPREADSHEET_TEMPLATE')}
          </HeaderDescription>
        </TeamMemberCSVWrapper>
        <BKJButton
          variant="PurpleOutlined"
          width="180px"
          onClick={onDownload}
          type="button"
          disabled={isDisabled}
        >
          {t('team_member:DOWNLOAD_CSV')}
        </BKJButton>
        <TeamMemberCSVWrapper>
          <UploadHeader>2. {t('team_member:UPLOAD_SPREADSHEET')}</UploadHeader>
          <HeaderDescription>
            {t('team_member:UPLOAD_SPREADSHEET_WITH_THE_NEW_RANKINGS')}
          </HeaderDescription>
        </TeamMemberCSVWrapper>
        <div style={{ width: '50%' }}>
          <BKJFileInput
            value={values?.csv}
            name="csv"
            onChange={onChange}
            accept=".csv"
            error={fieldErrors?.csv}
          />
        </div>
      </Flex>

      <Flex height="100%"></Flex>
      <BKJSideModalFooter justifyContent="space-between" flexDirection="row-reverse">
        <BKJButton
          variant="PurpleOutlined"
          width="180px"
          disabled={isDisabled || !values?.csv}
          onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
          type="button"
          isLoading={isSubmitting}
        >
          {t('team_member:SUBMIT')}
        </BKJButton>
      </BKJSideModalFooter>
    </Flex>
  );
};

export const TeamMemberEditRankingView: FC = () => {
  const {
    values: { location },
  } = useContext(TeamMemberPageContext);

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(location?.value)),
  );

  if (isMultiLocationTieringEnabled) {
    return (
      <CommunitySeniorityMethodProvider>
        <TeamMemberEditRankingDataProvider>
          <TeamMemberEditRankingFormProvider>
            <EditRankingViewMultiLocationTieringView />
          </TeamMemberEditRankingFormProvider>
        </TeamMemberEditRankingDataProvider>
      </CommunitySeniorityMethodProvider>
    );
  }

  return (
    <TeamMemberEditRankingFormProvider>
      <EditRankingViewTieringView />
    </TeamMemberEditRankingFormProvider>
  );
};
